import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, AlertTriangle, ChevronDown } from '../../ui/Icon';
import type { BudgetState, BudgetStatusItem, ExpenseCategory, Trip, User } from '../../../types';
import { tripMemberUsers } from '../../../utils/tripMembers';

export const BUDGET_CATEGORIES: ExpenseCategory[] = [
  'ACCOMMODATION', 'FLIGHTS', 'FOOD', 'ACTIVITIES', 'TRANSPORT', 'FUEL', 'PARKING', 'CASH', 'OTHER',
];

export type BudgetPerson = Pick<User, 'id' | 'username' | 'displayName'>;

// Everyone on the trip, the set a per-person cap can target. The owner is a
// participant row like anyone else (migration 20260811090000).
export function tripPeople(trip: Trip): BudgetPerson[] {
  return tripMemberUsers(trip);
}

const FILL: Record<BudgetState, string> = {
  ok:   'bg-teal-500',
  warn: 'bg-amber-500',
  over: 'bg-red-500',
};

// Track tints escalate with severity so an over-budget row reads as "critical"
// even before you read the numbers.
const TRACK: Record<BudgetState, string> = {
  ok:   'bg-slate-100 dark:bg-slate-800',
  warn: 'bg-amber-100/70 dark:bg-amber-950/30',
  over: 'bg-red-100 dark:bg-red-950/40',
};

const NUM: Record<BudgetState, string> = {
  ok:   'text-slate-700 dark:text-slate-300',
  warn: 'text-amber-600 dark:text-amber-400 font-semibold',
  over: 'text-red-600 dark:text-red-400 font-semibold',
};

const SUBTEXT: Record<BudgetState, string> = {
  ok:   'text-slate-400',
  warn: 'text-amber-600 dark:text-amber-400',
  over: 'text-red-600 dark:text-red-400',
};

const CHIP: Record<'warn' | 'over', string> = {
  warn: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300',
  over: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300',
};

/** Human label for a limit across the four dimensions. */
export function useBudgetLabel() {
  const { t } = useTranslation();
  return (item: Pick<BudgetStatusItem, 'scope' | 'category' | 'userId'>, people: BudgetPerson[]): string => {
    switch (item.scope) {
      case 'TOTAL': return t('expenses.budget.total');
      case 'DAILY': return t('expenses.budget.daily');
      case 'CATEGORY':
        return item.category
          ? t(`expenses.categories.${item.category}` as Parameters<typeof t>[0])
          : '—';
      case 'PERSON': {
        const p = people.find(x => x.id === item.userId);
        return p?.displayName || p?.username || '—';
      }
    }
  };
}

/** The right-hand remainder text for a limit, shared by the bar and the alert
 *  banner so "left / over / at-limit" wording never diverges between them. At (or
 *  a sub-unit sliver over) the cap it reads "limit reached" instead of the
 *  nonsensical "over by 0" / "0 left". */
export function useBudgetRemainderText() {
  const { t } = useTranslation();
  return (item: Pick<BudgetStatusItem, 'state' | 'used' | 'limit'>, currency: string): string => {
    if (item.state === 'over') {
      const overRounded = Math.round(item.used - item.limit);
      return overRounded >= 1
        ? t('expenses.budget.overAmount', { amount: `${overRounded} ${currency}` })
        : t('expenses.budget.atLimit');
    }
    const remaining = item.limit - item.used;
    return Math.round(remaining) <= 0
      ? t('expenses.budget.atLimit')
      : t('expenses.budget.leftAmount', { amount: `${remaining.toFixed(0)} ${currency}` });
  };
}

interface BarProps {
  label: string;
  item: BudgetStatusItem;
  currency: string;
}

// State of a single day against the per-day cap — mirrors the server's stateFor
// so the DAILY breakdown colours each day the same way the summary bar is.
function dayState(used: number, limit: number): BudgetState {
  if (limit <= 0) return 'ok';
  if (used - limit > 0.005) return 'over';
  return (used / limit) * 100 >= 80 ? 'warn' : 'ok';
}

/** One day of a DAILY cap's breakdown: date · mini meter · amount, coloured by
 *  that day's own state so over/close days stand out at a glance. */
function DayBreakdownRow({ date, used, limit, currency }: { date: string; used: number; limit: number; currency: string }) {
  const st = dayState(used, limit);
  const fill = Math.min(limit > 0 ? (used / limit) * 100 : 0, 100);
  const dateLabel = new Date(date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10.5px] text-slate-500 dark:text-slate-400 w-16 shrink-0 truncate">{dateLabel}</span>
      <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${TRACK[st]}`}>
        <div className={`h-full rounded-full ${FILL[st]}`} style={{ width: `${fill}%` }} />
      </div>
      <span className={`font-mono text-[10.5px] tabular-nums shrink-0 ${NUM[st]}`}>
        {used.toFixed(0)}<span className="text-slate-400"> / {limit.toFixed(0)} {currency}</span>
      </span>
    </div>
  );
}

/** A single labelled budget meter: teal → amber (≥80%) → red (>100%). Status is
 *  carried by number colour, a tinted track, an icon and a %-chip — never colour
 *  alone. A DAILY cap (whose bar collapses to the worst day) is expandable into a
 *  per-day breakdown. */
export function BudgetBar({ label, item, currency }: BarProps) {
  const { t } = useTranslation();
  const remainderText = useBudgetRemainderText();
  const [expanded, setExpanded] = useState(false);
  const pctRaw = item.limit > 0 ? (item.used / item.limit) * 100 : 0;
  const fillPct = Math.min(pctRaw, 100);
  const flagged = item.state !== 'ok';
  const breakdown = item.scope === 'DAILY' ? item.dailyBreakdown ?? [] : [];
  const canExpand = breakdown.length > 0;

  const headerRow = (
    <div className="flex items-baseline justify-between gap-2 mb-1.5">
      <span className="flex items-center gap-1 min-w-0 text-sm text-slate-700 dark:text-slate-300">
        <span className="truncate">{label}</span>
        {canExpand && (
          <Icon as={ChevronDown} size={13} className={`text-slate-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        )}
      </span>
      <span className="font-mono text-xs tabular-nums shrink-0">
        <span className={NUM[item.state]}>{item.used.toFixed(0)}</span>
        <span className="text-slate-400"> / {item.limit.toFixed(0)}</span>
        <span className="text-slate-400 text-[10px]"> {currency}</span>
      </span>
    </div>
  );

  return (
    <div className="min-w-0">
      {canExpand ? (
        <button type="button" onClick={() => setExpanded(e => !e)} className="w-full text-left cursor-pointer">
          {headerRow}
        </button>
      ) : headerRow}
      <div className={`h-2 rounded-full overflow-hidden ${TRACK[item.state]}`}>
        <div
          className={`h-full rounded-full transition-all duration-500 ${FILL[item.state]}`}
          style={{ width: `${fillPct}%` }}
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span className={`inline-flex items-center gap-1 text-[11px] ${SUBTEXT[item.state]}`}>
          {item.state === 'over' && <Icon as={AlertTriangle} size={11} />}
          {remainderText(item, currency)}
          {item.scope === 'DAILY' && item.overDays && item.overDays.length > 0 && (
            <> · {t('expenses.budget.daysOver', { count: item.overDays.length })}</>
          )}
        </span>
        {flagged && (
          <span className={`shrink-0 font-mono text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full ${CHIP[item.state as 'warn' | 'over']}`}>
            {Math.round(pctRaw)}%
          </span>
        )}
      </div>
      {canExpand && expanded && (
        <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 space-y-1.5">
          {breakdown.map(d => (
            <DayBreakdownRow key={d.date} date={d.date} used={d.used} limit={item.limit} currency={currency} />
          ))}
        </div>
      )}
    </div>
  );
}
